/**
 * The guest demo's stale-client check (story `q-live-sword-grip-still-open`).
 *
 * The owner, 2026-10-23, on the live demo on a phone: "the sword grip is still broken", hours after
 * AD-838 shipped. The deployed bundle closes the fist (measured: the live bytes, served to a phone
 * viewport, draw a closed hand), but nothing made a page that was already open pick that bundle up.
 * "Play again" starts a fresh guest session IN THE SAME PAGE, the client carried no build stamp,
 * and the edge sent the page with no Cache-Control, so a tab opened before a deploy keeps playing
 * the old client against new content and new art for as long as it stays open.
 *
 * So every guest start asks the server which build it is (`/healthz` `sha`, the same stamp the
 * bundle carries) and reloads the page when they differ. At most ONE reload per served build per
 * tab (`sessionStorage`): if the reload still brings back the old bundle (an edge or cache fault),
 * the visitor plays rather than looping, and post-deploy verify reports the delivery fault.
 * Only a build sha is stored; the guest token never leaves page memory (guestFlow.ts).
 */

import { isStaleBuild } from '../version.js';

/** Remembers which served build this tab already reloaded for. */
export const STALE_RELOAD_KEY = 'gc-stale-client-reload';

export interface StaleClientDeps {
  /** GET a URL; the real one is `fetch` with `cache: 'no-store'`. */
  fetchJson: (url: string) => Promise<unknown>;
  /** The tab's session storage (or a fake); null when the browser refuses it. */
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  /** This client's own stamp; defaults to the bundle's. */
  client?: string;
}

/** The build the server reports, or null when it cannot be read. */
export async function servedBuild(
  deps: Pick<StaleClientDeps, 'fetchJson'>,
): Promise<string | null> {
  try {
    const body = await deps.fetchJson('/healthz');
    const sha = (body as { sha?: unknown } | null)?.sha;
    return typeof sha === 'string' ? sha : null;
  } catch {
    return null;
  }
}

/**
 * True when this page should reload before starting a guest session: the server is on another
 * build AND this tab has not already reloaded for that build. Records the attempt when true.
 */
export async function shouldReloadForNewBuild(deps: StaleClientDeps): Promise<boolean> {
  const served = await servedBuild(deps);
  if (!isStaleBuild(served, deps.client)) return false;
  if (deps.storage === null) return false; // no way to bound the reloads: never risk a loop
  try {
    if (deps.storage.getItem(STALE_RELOAD_KEY) === served) return false;
    deps.storage.setItem(STALE_RELOAD_KEY, served as string);
  } catch {
    return false;
  }
  return true;
}

/** The browser wiring: `/healthz` uncached, and this tab's sessionStorage. */
export function browserStaleClientDeps(): StaleClientDeps {
  let storage: StaleClientDeps['storage'] = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  return {
    fetchJson: async (url) => {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`); // id-ok: HTTP status number
      return (await res.json()) as unknown;
    },
    storage,
  };
}
